// src/utils/leagueStarState.js
//
// League Battle View V2 — per-star live "disposition" derivation (Phase 1, pure
// + node-clean). Maps a holding's current scorer reading to one of the locked
// design states: heating · edge · hit · danger · busted · quiet.
//
// SCORING DISCIPLINE (BUILD_RULES §4): no new threshold math. The hit/bust
// boundaries cite THRESHOLD_MULTIPLIERS (1.0 / -1.0) directly, and the
// "approaching a line" edge/danger zones REUSE the canonical detectRedZone
// (baggerBombUtils.js:182-233 — the 75%-of-next-threshold zone-start). If the
// design ever retunes that proximity, it changes in one canonical place.
//
// DIRECTION: the multiplier and badges handed in are ALREADY direction-correct
// — calculateAssetScoreV3 negates for shorts internally (baggerBombUtils.js:
// 538-552). `direction` is carried for presentation only; we do NOT re-negate
// here (that would double-negate shorts — the bug flat6BattleEnrichment.js:84-92
// warns against).

import { detectRedZone } from './baggerBombUtils';
import { THRESHOLD_MULTIPLIERS } from '../constants/baggerBombScoring';
import { CAPTURE_STATE } from '../constants/leagueTournament';

const POSITIVE_BADGES = new Set(['bagger', 'doubleBagger', 'tenBagger']);
const NEGATIVE_BADGES = new Set(['bust', 'crash', 'meltdown']);

// Spec §1.1 (canonical-open policy) — the user-leg SETTLEMENT states, a display
// axis ORTHOGONAL to the disposition state above (heating/hit/busted/…). These
// describe where the leg sits in the capture→bank lifecycle, so a Day-0 pick
// awaiting the open reads "waiting for the open" instead of a broken-looking
// +0.0×. First-class, never a mult:0 coincidence. Only canonical rounds carry
// them; a legacy/absent-policy round derives `null` and renders exactly as today.
export const SETTLE_STATE = Object.freeze({
  PENDING: 'pending',     // baseline not yet captured (null / PENDING_OPEN)
  ESTIMATED: 'estimated', // captured, scoring live, today not yet banked
  OFFICIAL: 'official',   // the day is banked (score of record exists) / fully settled
  VOID: 'void',           // NO_ELIGIBLE_OPEN — terminal, no contribution, no penalty
});

/**
 * The user leg's canonical-open settlement state (or null for legacy rounds).
 * Reads the pick's LIVE (last) leg — the one the meter animates.
 *
 * Boundary (surfaced for review — Spec Deliverable 1): the estimated↔official
 * flip keys off `dayBanked` (is TODAY's ET date already banked?). An open,
 * captured leg reads `estimated` intraday and `official` once the day banks;
 * it RE-ENTERS `estimated` when the next session opens (dayBanked is per-today).
 * A fully-closed leg (no open leg) is always `official`. The displayed
 * multiplier is the live scorePick reading in BOTH states (the meter is a
 * live-quote transform; the authoritative banked percentile-ATR value lives in
 * the standings, never the live star) — the solid/"banked" vs dashed/"est"
 * styling is the state signal.
 *
 * @param {Object} pick - { legs[] }
 * @param {{ canonicalPolicy?: boolean, dayBanked?: boolean }} [opts]
 * @returns {'pending'|'estimated'|'official'|'void'|null}
 */
export function deriveSettleState(pick, { canonicalPolicy = false, dayBanked = false } = {}) {
  if (!canonicalPolicy) return null; // legacy — no new states; render as today
  const legs = pick?.legs || [];
  const liveLeg = legs.length > 0 ? legs[legs.length - 1] : null;
  if (!liveLeg) return null;
  if (liveLeg.captureState === CAPTURE_STATE.NO_ELIGIBLE_OPEN) return SETTLE_STATE.VOID;
  const isOpen = liveLeg.closedAt === undefined;
  const hasBaseline = Number.isFinite(liveLeg.baselinePrice) && liveLeg.baselinePrice > 0;
  if (isOpen && !hasBaseline) return SETTLE_STATE.PENDING;
  if (!isOpen || dayBanked) return SETTLE_STATE.OFFICIAL;
  return SETTLE_STATE.ESTIMATED;
}

/**
 * The star's live disposition. Precedence (first match wins):
 *   1. busted — a bust tier crossed (badge set) OR multiplier ≤ -1.0. Sticky.
 *   2. hit    — a bagger tier crossed (badge set) OR multiplier ≥ 1.0. Sticky:
 *               a star that popped then pulled back to 0.9 still reads `hit`.
 *   3. danger — no badge yet, inside the bust red zone (detectRedZone negative).
 *   4. edge   — no badge yet, inside the bagger red zone (detectRedZone positive).
 *   5. heating— positive drift below the edge zone (0 < multiplier < zone start).
 *   6. quiet  — flat / small negative wobble (the default).
 *
 * Edge case (documented): a star that crossed a bust tier reads `busted` even if
 * it earlier earned a bagger — the down-move is the louder current signal.
 *
 * `prevState` is accepted (and ignored in V1) so the meter can diff it for
 * `justIn` in ONE place; deriveStarState stays a pure function of the CURRENT
 * reading, referentially transparent.
 *
 * @param {{ multiplier?: number, badges?: string[], direction?: string, prevState?: string|null }} args
 * @returns {'hit'|'busted'|'edge'|'danger'|'heating'|'quiet'}
 */
export function deriveStarState({ multiplier = 0, badges = [], direction = 'long', prevState = null } = {}) {
  void direction; // presentation only — multiplier/badges are already direction-correct
  void prevState; // diffed by the caller (justIn), not here
  const mult = Number.isFinite(multiplier) ? multiplier : 0;
  const list = Array.isArray(badges) ? badges : [];

  const hasBust = list.some((b) => NEGATIVE_BADGES.has(b));
  const hasBagger = list.some((b) => POSITIVE_BADGES.has(b));

  if (hasBust || mult <= THRESHOLD_MULTIPLIERS.bust) return 'busted';
  if (hasBagger || mult >= THRESHOLD_MULTIPLIERS.bagger) return 'hit';

  const zone = detectRedZone(mult, list);
  if (zone?.direction === 'negative') return 'danger';
  if (zone?.direction === 'positive') return 'edge';

  if (mult > 0) return 'heating';
  return 'quiet';
}
